import { Box, Typography, Collapse } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotesIcon from '@mui/icons-material/Notes';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import type { AlertItem, AlertSeverity } from '../api/types';
import { Badge, formatAlertTimestamp } from './shared';
import {
    formatCompactUsd,
    formatSignedCompactUsd,
    formatSignedPercent,
} from '../utils/format';
import { alertSeverityColor } from '../utils/colors';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AlertRowVariant = 'sidebar' | 'overview';

export interface AlertRowProps {
    alert: AlertItem;
    marketQuestion: string;
    marketColor?: string;
    onClickMarket?: () => void;
    expanded?: boolean;
    onToggleExpand?: () => void;
    /**
     * Render in narrow mode: uses a 2x2 grid (instead of 4x1) in the expanded panel.
     * Default false.
     */
    narrow?: boolean;
    /** @deprecated No-op. Both variants render identically; retained for API stability. */
    variant?: AlertRowVariant;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_META: Record<
    AlertSeverity,
    {
        icon: typeof WarningAmberIcon;
        palette: 'error' | 'warning' | 'info' | 'default';
    }
> = {
    critical: { icon: ErrorOutlineIcon, palette: 'error' },
    warning: { icon: WarningAmberIcon, palette: 'warning' },
    info: { icon: InfoOutlinedIcon, palette: 'info' },
    log: { icon: NotesIcon, palette: 'default' },
};

export const ALERT_TYPE_LABELS: Record<string, string> = {
    insider_capital_threshold: 'Insider Capital',
    new_insider_admission: 'New Insider',
    insider_position_closed: 'Position Closed',
    large_position_change: 'Position Change',
    large_trade: 'Large Trade',
    probability_spike: 'Prob Spike',
    volume_anomaly: 'Volume Anomaly',
    gate_request: 'Gate Request',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Lead metric (collapsed row, right-aligned column)
// ---------------------------------------------------------------------------

export interface LeadMetric {
    value: string;
    tone: 'pos' | 'neg' | 'neutral';
    side?: 'YES' | 'NO';
}

function parseSide(v: unknown): 'YES' | 'NO' | undefined {
    if (typeof v !== 'string') return undefined;
    const s = v.trim().toUpperCase();
    if (s === 'YES' || s === 'Y') return 'YES';
    if (s === 'NO' || s === 'N') return 'NO';
    return undefined;
}

function num(v: unknown): number | null {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function getLeadMetric(alert: AlertItem): LeadMetric {
    const ctx = alert.context;

    switch (alert.alert_type) {
        case 'insider_capital_threshold': {
            const yes = num(ctx.capital_usd_yes);
            const no = num(ctx.capital_usd_no);
            if (yes != null && no != null) {
                const yesLarger = yes >= no;
                const amount = yesLarger ? yes : no;
                return {
                    value: formatCompactUsd(amount),
                    tone: 'neutral',
                    side: yesLarger ? 'YES' : 'NO',
                };
            }
            break;
        }
        case 'probability_spike':
        case 'probability_change': {
            const d = num(ctx.delta_pct);
            if (d != null) {
                return {
                    value: formatSignedPercent(d),
                    tone: d >= 0 ? 'pos' : 'neg',
                };
            }
            break;
        }
        case 'volume_anomaly':
        case 'activity_surge': {
            const m = num(ctx.multiplier);
            if (m != null)
                return { value: `${m.toFixed(1)}×`, tone: 'neutral' };
            break;
        }
        case 'large_trade': {
            const s = num(ctx.size_usd);
            if (s != null)
                return {
                    value: formatCompactUsd(s),
                    tone: 'neutral',
                    side: parseSide(ctx.side),
                };
            break;
        }
        case 'large_position_change': {
            const d = num(ctx.delta_usd);
            if (d != null)
                return {
                    value: formatSignedCompactUsd(d),
                    tone: d >= 0 ? 'pos' : 'neg',
                    side: parseSide(ctx.side),
                };
            break;
        }
        case 'new_insider_admission': {
            const s = num(ctx.size_usd) ?? num(ctx.value_usd);
            if (s != null)
                return {
                    value: formatCompactUsd(s),
                    tone: 'neutral',
                    side: parseSide(ctx.side),
                };
            break;
        }
        case 'insider_position_closed': {
            const p = num(ctx.realized_pnl_usd);
            if (p != null)
                return {
                    value: formatSignedCompactUsd(p),
                    tone: p >= 0 ? 'pos' : 'neg',
                    side: parseSide(ctx.side),
                };
            break;
        }
    }

    // Fallback: current_yes_prob as integer percent
    const p = num(alert.current_yes_prob);
    if (p == null) return { value: '—', tone: 'neutral' };
    return { value: `${Math.round(p * 100)}%`, tone: 'neutral' };
}

// ---------------------------------------------------------------------------
// Context cells (expanded panel grid)
// ---------------------------------------------------------------------------

export interface ContextCell {
    label: string;
    value: string;
}

const DASH = '—';

function fmtUsdOrDash(v: unknown): string {
    const n = num(v);
    return n == null ? DASH : formatCompactUsd(n);
}

function fmtPctOrDash(v: unknown): string {
    const n = num(v);
    return n == null ? DASH : `${Math.round(n * 100)}%`;
}

function fmtSignedPctOrDash(v: unknown): string {
    const n = num(v);
    return n == null ? DASH : formatSignedPercent(n);
}

function fmtMinutesOrDash(v: unknown): string {
    const n = num(v);
    return n == null ? DASH : `${n}m`;
}

function fmtMultiplierOrDash(v: unknown): string {
    const n = num(v);
    return n == null ? DASH : `${n.toFixed(1)}×`;
}

function fmtAnyScalar(v: unknown): string {
    if (v == null) return DASH;
    if (typeof v === 'number') {
        if (Math.abs(v) >= 1_000) return formatCompactUsd(v);
        return String(v);
    }
    return String(v);
}

export function getContextCells(alert: AlertItem): ContextCell[] {
    const ctx = alert.context ?? {};

    switch (alert.alert_type) {
        case 'insider_capital_threshold': {
            const ny = num(ctx.n_traders_yes);
            const nn = num(ctx.n_traders_no);
            return [
                { label: 'YES Cap', value: fmtUsdOrDash(ctx.capital_usd_yes) },
                { label: 'NO Cap', value: fmtUsdOrDash(ctx.capital_usd_no) },
                {
                    label: 'Traders',
                    value: ny != null && nn != null ? `${ny} / ${nn}` : DASH,
                },
                {
                    label: 'Threshold',
                    value: fmtUsdOrDash(ctx.threshold_triggered),
                },
            ];
        }
        case 'probability_spike':
        case 'probability_change':
            return [
                { label: 'Δ Prob', value: fmtSignedPctOrDash(ctx.delta_pct) },
                { label: 'Window', value: fmtMinutesOrDash(ctx.window_min) },
                { label: 'Volume', value: fmtUsdOrDash(ctx.volume_usd) },
                {
                    label: 'Current',
                    value: fmtPctOrDash(alert.current_yes_prob),
                },
            ];
        case 'volume_anomaly':
        case 'activity_surge':
            return [
                {
                    label: 'Multiplier',
                    value: fmtMultiplierOrDash(ctx.multiplier),
                },
                { label: 'Window', value: fmtMinutesOrDash(ctx.window_min) },
                {
                    label: 'Current Vol',
                    value: fmtUsdOrDash(ctx.current_volume_usd),
                },
                {
                    label: 'Baseline',
                    value: fmtUsdOrDash(ctx.baseline_volume_usd),
                },
            ];
        case 'large_trade':
            return [
                { label: 'Size', value: fmtUsdOrDash(ctx.size_usd) },
                {
                    label: 'Side',
                    value: typeof ctx.side === 'string' ? ctx.side : DASH,
                },
                { label: 'Price', value: fmtPctOrDash(ctx.price) },
                {
                    label: 'Trader',
                    value: typeof ctx.trader === 'string' ? ctx.trader : DASH,
                },
            ];
        case 'large_position_change': {
            const d = num(ctx.delta_usd);
            return [
                {
                    label: 'Δ Position',
                    value: d != null ? formatSignedCompactUsd(d) : DASH,
                },
                {
                    label: 'New Total',
                    value: fmtUsdOrDash(ctx.new_position_usd),
                },
                {
                    label: 'Side',
                    value: typeof ctx.side === 'string' ? ctx.side : DASH,
                },
                {
                    label: 'Trader',
                    value: typeof ctx.trader === 'string' ? ctx.trader : DASH,
                },
            ];
        }
        case 'new_insider_admission':
        case 'insider_position_closed': {
            const pnl = num(ctx.realized_pnl_usd);
            return [
                {
                    label: 'Position',
                    value: fmtUsdOrDash(ctx.size_usd ?? ctx.value_usd),
                },
                {
                    label: 'Side',
                    value: typeof ctx.side === 'string' ? ctx.side : DASH,
                },
                { label: 'Entry Prob', value: fmtPctOrDash(ctx.entry_prob) },
                {
                    label: 'PnL',
                    value: pnl != null ? formatSignedCompactUsd(pnl) : DASH,
                },
            ];
        }
    }

    // Unknown alert type: humanize and dump up to 4 scalar entries
    const cells: ContextCell[] = [];
    for (const [key, value] of Object.entries(ctx)) {
        if (cells.length >= 4) break;
        if (value == null || typeof value === 'object') continue;
        cells.push({
            label: key.replace(/_/g, ' '),
            value: fmtAnyScalar(value),
        });
    }
    return cells;
}

// ---------------------------------------------------------------------------
// Visual helpers
// ---------------------------------------------------------------------------

function toneColor(
    tone: 'pos' | 'neg' | 'neutral',
    palette: {
        success: { main: string };
        error: { main: string };
        text: { primary: string };
    }
): string {
    if (tone === 'pos') return palette.success.main;
    if (tone === 'neg') return palette.error.main;
    return palette.text.primary;
}

function formatAbsoluteTimestamp(isoStr: string): string {
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return '—'; // match the collapsed row's guard
    const date = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
    const time = d.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    return `${date}, ${time}`;
}

// ---------------------------------------------------------------------------
// AlertRow
// ---------------------------------------------------------------------------

export function AlertRow({
    alert,
    marketQuestion,
    marketColor,
    onClickMarket,
    expanded,
    onToggleExpand,
    narrow = false,
}: AlertRowProps) {
    const theme = useTheme();
    const meta = SEVERITY_META[alert.severity] ?? SEVERITY_META.info;
    const sevColor = alertSeverityColor(
        alert.severity,
        theme.palette.text.secondary
    );
    const Icon = meta.icon;
    const typeLabel = (ALERT_TYPE_LABELS[alert.alert_type] ?? alert.alert_type)
        .replace(/[_-]/g, ' ')
        .toUpperCase();
    const lead = getLeadMetric(alert);
    const cells = getContextCells(alert);
    const leadColor = toneColor(lead.tone, theme.palette);

    return (
        <Box
            sx={{
                borderBottom: 1,
                borderColor: alpha(theme.palette.divider, 0.3),
                borderLeft: `3px solid ${sevColor}`,
                bgcolor: expanded
                    ? alpha(sevColor, 0.06)
                    : alpha(sevColor, 0.04),
                transition: 'background 0.15s',
            }}
        >
            {/* Collapsed row — two lines, TradeRow style */}
            <Box
                onClick={onToggleExpand}
                sx={{
                    py: 0.75,
                    px: 1,
                    cursor: onToggleExpand ? 'pointer' : 'default',
                    '&:hover': onToggleExpand
                        ? { bgcolor: alpha(theme.palette.action.hover, 0.08) }
                        : {},
                    transition: 'background 0.15s',
                }}
            >
                {/* Line 1: severity icon + market question (+ market dot) + timestamp */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.25,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            overflow: 'hidden',
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        <Icon
                            sx={{
                                fontSize: 14,
                                color: sevColor,
                                flexShrink: 0,
                            }}
                        />
                        {marketColor && (
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    minWidth: 6,
                                    borderRadius: '50%',
                                    bgcolor: marketColor,
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Typography
                            component="span"
                            onClick={
                                onClickMarket
                                    ? (e: React.MouseEvent) => {
                                          e.stopPropagation();
                                          onClickMarket();
                                      }
                                    : undefined
                            }
                            sx={{
                                fontSize: '11px',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: onClickMarket ? 'pointer' : 'default',
                                '&:hover': onClickMarket
                                    ? { color: 'primary.main' }
                                    : {},
                            }}
                        >
                            {marketQuestion}
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: 'text.disabled',
                            fontFamily: 'monospace',
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {formatAlertTimestamp(alert.ts)}
                    </Typography>
                </Box>

                {/* Line 2: type badge + side badge (YES/NO) + lead metric + chevron */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        fontSize: '10px',
                    }}
                >
                    <Badge color={sevColor}>{typeLabel}</Badge>
                    {lead.side && (
                        <Badge
                            color={
                                lead.side === 'YES'
                                    ? theme.palette.success.main
                                    : theme.palette.error.main
                            }
                        >
                            {lead.side}
                        </Badge>
                    )}
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            color: leadColor,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {lead.value}
                    </Typography>
                    <KeyboardArrowRightIcon
                        sx={{
                            fontSize: 14,
                            color: 'text.disabled',
                            ml: 'auto',
                            transform: expanded
                                ? 'rotate(90deg)'
                                : 'rotate(0deg)',
                            transition: 'transform 0.15s',
                        }}
                    />
                </Box>
            </Box>

            {/* Expanded panel */}
            <Collapse in={expanded} unmountOnExit>
                <Box sx={{ px: 1, pb: 1, pt: 0.25 }}>
                    {/* Title */}
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: 'text.primary',
                            mb: alert.detail ? 0.5 : 0.75,
                            lineHeight: 1.3,
                        }}
                    >
                        {alert.title}
                    </Typography>

                    {/* Detail paragraph */}
                    {alert.detail && (
                        <Typography
                            sx={{
                                fontSize: '11px',
                                color: 'text.secondary',
                                lineHeight: 1.5,
                                mb: 1,
                            }}
                        >
                            {alert.detail}
                        </Typography>
                    )}

                    {/* Triggered price */}
                    {alert.current_yes_prob != null && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                mb: 0.75,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: 'text.disabled',
                                    fontFamily: 'monospace',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                Probability at trigger
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    fontFamily: 'monospace',
                                    color: sevColor,
                                }}
                            >
                                {Math.round(alert.current_yes_prob * 100)}%
                            </Typography>
                        </Box>
                    )}

                    {/* Context grid */}
                    {cells.length > 0 && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: narrow
                                    ? 'repeat(2, 1fr)'
                                    : `repeat(${Math.min(cells.length, 4)}, 1fr)`,
                                gap: '1px',
                                bgcolor: alpha(theme.palette.divider, 0.4),
                                border: 1,
                                borderColor: alpha(theme.palette.divider, 0.4),
                                mb: 1,
                            }}
                        >
                            {cells.map(cell => (
                                <Box
                                    key={cell.label}
                                    sx={{
                                        bgcolor: theme.palette.background.paper,
                                        px: 1,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '9px',
                                            color: 'text.disabled',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.4,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {cell.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            fontFamily:
                                                'ui-monospace, SFMono-Regular, monospace',
                                            fontWeight: 600,
                                            color: 'text.primary',
                                            mt: 0.25,
                                        }}
                                    >
                                        {cell.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Footer row */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 0.5,
                        }}
                    >
                        {onClickMarket ? (
                            <Typography
                                component="button"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onClickMarket();
                                }}
                                sx={{
                                    fontSize: '11px',
                                    color: 'primary.main',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Open market →
                            </Typography>
                        ) : (
                            <span />
                        )}
                        <Typography
                            sx={{
                                fontSize: '10px',
                                fontFamily:
                                    'ui-monospace, SFMono-Regular, monospace',
                                color: 'text.disabled',
                            }}
                        >
                            {formatAbsoluteTimestamp(alert.ts)}
                        </Typography>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
}
